import { api, request } from './client.js';

const root = '/admin/ai';

export const aiApi = {
  // Personality
  listPersonalities: (params) => api.get(`${root}/personality`, { query: params }),
  getActivePersonality: () => api.get(`${root}/personality/active`),
  getPersonality: (id) => api.get(`${root}/personality/${id}`),
  createPersonality: (body) => api.post(`${root}/personality`, body),
  updatePersonality: (id, body) => api.patch(`${root}/personality/${id}`, body),
  activatePersonality: (id) => api.post(`${root}/personality/${id}/activate`),
  deletePersonality: (id) => api.delete(`${root}/personality/${id}`),
  getPersonalityAssignments: () => api.get(`${root}/personality/assignments`),
  setPersonalityAssignments: (assignments) => api.put(`${root}/personality/assignments`, { assignments }),
  getAgents: () => api.get(`${root}/agents`),
  setAgent: (agentId, body) => api.put(`${root}/agents/${agentId}`, body),
  createAgent: (body) => api.post(`${root}/agents`, body),
  updateAgentDefinition: (agentId, body) => api.patch(`${root}/agents/${agentId}`, body),
  deleteAgent: (agentId) => api.delete(`${root}/agents/${agentId}`),

  // Usage + budget + breaker
  listUsage: (params) => api.get(`${root}/usage`, { query: params }),
  usageOverview: (params) => api.get(`${root}/usage/overview`, { query: params }),
  budgetStatus: () => api.get(`${root}/usage/budget`),
  breakerStatus: () => api.get(`${root}/breaker`),

  // Model catalog and per context assignments
  models: () => api.get(`${root}/models`),
  setModelsCatalog: (models) => api.put(`${root}/models/catalog`, { models }),
  setModelAssignments: (body) => api.put(`${root}/models/assignments`, body),

  // Feedback review and learning loop
  feedbackList: (params) => api.get(`${root}/feedback`, { query: params }),
  feedbackStats: () => api.get(`${root}/feedback/stats`),
  feedbackPromote: (messageId, body) => api.post(`${root}/feedback/${messageId}/promote`, body || {}),
  feedbackUnpromote: (messageId) => api.post(`${root}/feedback/${messageId}/unpromote`, {}),
  feedbackResolve: (messageId, resolved = true) => api.post(`${root}/feedback/${messageId}/resolve`, { resolved }),

  // Jailbreak evaluation (prompt-injection resistance test)
  jailbreakEstimate: (agentId) => api.post(`${root}/jailbreak/estimate`, { agentId }),
  jailbreakRun: (agentId) => api.post(`${root}/jailbreak/run`, { agentId, confirm: true }),
  jailbreakReports: (params) => api.get(`${root}/jailbreak/reports`, { query: params }),
  jailbreakReport: (id) => api.get(`${root}/jailbreak/reports/${id}`),
  jailbreakReportDelete: (id) => api.delete(`${root}/jailbreak/reports/${id}`),

  // Playground
  playground: (body) => api.post(`${root}/playground`, body),
  playgroundAgent: (body) => api.post(`${root}/playground/agent`, body),
  playgroundOptions: () => api.get(`${root}/playground/options`),

  // Speech to text dictation. `formData` carries the `audio` file and
  // optional `language` hint.
  transcribe: (formData) => api.post(`${root}/transcribe`, formData),

  /**
   * Streaming chat completion over Server Sent Events.
   * Calls onDelta(text) for every chunk and resolves with the final
   * metadata frame ({ usage, costUsd, model, finishReason }).
   */
  async playgroundStream(body, { onDelta, signal } = {}) {
    const res = await request(`${root}/playground/stream`, {
      method: 'POST',
      body,
      raw: true,
      signal
    });
    if (!res.ok || !res.body) {
      const err = new Error('Stream request failed');
      err.statusCode = res.status;
      throw err;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let final = null;

    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buffer.indexOf('\n')) >= 0) {
        const line = buffer.slice(0, idx).trim();
        buffer = buffer.slice(idx + 1);
        if (!line.startsWith('data:')) continue;
        const payload = line.slice(5).trim();
        if (payload === '[DONE]') return final;
        let evt;
        try { evt = JSON.parse(payload); } catch { continue; }
        if (evt.type === 'delta' && typeof evt.text === 'string') {
          if (typeof onDelta === 'function') onDelta(evt.text);
        } else if (evt.type === 'done') {
          final = evt;
        } else if (evt.type === 'error') {
          const err = new Error(evt.message || 'Stream error');
          err.code = 'STREAM_ERROR';
          throw err;
        }
      }
    }
    return final;
  },

  /**
   * Streaming agent playground over SSE. Calls onEvent(evt) for every frame
   * (stage / agent / tool / delta) so the UI can show the live pipeline, and
   * resolves with the final `done` frame.
   */
  async playgroundAgentStream(body, { onEvent, signal } = {}) {
    const res = await request(`${root}/playground/agent/stream`, {
      method: 'POST',
      body,
      raw: true,
      signal
    });
    if (!res.ok || !res.body) {
      const err = new Error('Stream request failed');
      err.statusCode = res.status;
      throw err;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let final = null;

    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buffer.indexOf('\n')) >= 0) {
        const line = buffer.slice(0, idx).trim();
        buffer = buffer.slice(idx + 1);
        if (!line.startsWith('data:')) continue;
        const payload = line.slice(5).trim();
        if (payload === '[DONE]') return final;
        let evt;
        try { evt = JSON.parse(payload); } catch { continue; }
        if (evt.type === 'done') {
          final = evt;
        } else if (evt.type === 'error') {
          const err = new Error(evt.message || 'Stream error');
          err.code = 'STREAM_ERROR';
          throw err;
        } else if (typeof onEvent === 'function') {
          onEvent(evt);
        }
      }
    }
    return final;
  }
};
